(() => {
  const componentName = 'vl-review-card-slider';

  if (customElements.get(componentName)) return;

  class VLReviewCardSlider extends HTMLElement {
    constructor() {
      super();

      this.isInitialized = false;
      this.resizeObserver = null;
      this.scrollFrame = null;

      this.handlePrevious = this.handlePrevious.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.updateOverflowState = this.updateOverflowState.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.track = this.querySelector('[data-vl-review-card-slider-track]');
      this.slides = Array.from(this.querySelectorAll('[data-vl-review-card-slider-slide]'));
      this.previousButton = this.querySelector('[data-vl-review-card-slider-previous]');
      this.nextButton = this.querySelector('[data-vl-review-card-slider-next]');

      if (!this.track || !this.slides.length) return;

      this.previousButton?.addEventListener('click', this.handlePrevious);
      this.nextButton?.addEventListener('click', this.handleNext);
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      this.track.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateOverflowState);
        this.resizeObserver.observe(this.track);
      }

      this.isInitialized = true;
      this.updateOverflowState();
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      this.previousButton?.removeEventListener('click', this.handlePrevious);
      this.nextButton?.removeEventListener('click', this.handleNext);
      this.track?.removeEventListener('scroll', this.handleScroll);
      this.track?.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.resizeObserver?.disconnect();

      if (this.scrollFrame !== null) {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }

      this.isInitialized = false;
    }

    handlePrevious() {
      this.scrollBySlide(-1);
    }

    handleNext() {
      this.scrollBySlide(1);
    }

    handleScroll() {
      if (this.scrollFrame !== null) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateControls();
      });
    }

    handleKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      this.scrollBySlide(direction);
    }

    handleBlockSelect(event) {
      const selectedSlide = event.target.closest('[data-vl-review-card-slider-slide]');
      if (!selectedSlide || !this.contains(selectedSlide)) return;

      this.scrollToSlide(Number(selectedSlide.dataset.vlReviewCardSliderIndex), false);
    }

    getNearestSlideIndex() {
      const trackLeft = this.track.getBoundingClientRect().left + this.getTrackInset();
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    }

    scrollBySlide(direction) {
      const step = this.getSlideStep();
      if (!step) return;

      this.track.scrollBy({
        left: step * direction,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }

    getSlideStep() {
      if (this.slides.length < 2) return this.slides[0]?.getBoundingClientRect().width || 0;

      return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
    }

    scrollToSlide(index, smooth = true) {
      const safeIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      const targetSlide = this.slides[safeIndex];
      if (!targetSlide) return;

      const trackRect = this.track.getBoundingClientRect();
      const slideRect = targetSlide.getBoundingClientRect();

      this.track.scrollTo({
        left: this.track.scrollLeft + slideRect.left - trackRect.left - this.getTrackInset(),
        behavior: smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto',
      });
    }

    getTrackInset() {
      return Number.parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
    }

    updateOverflowState() {
      const hasOverflow = this.track.scrollWidth > this.track.clientWidth + 1;
      this.classList.toggle('is-scrollable', hasOverflow);
      this.track.setAttribute('aria-roledescription', hasOverflow ? 'carousel' : 'group');
      this.updateControls();
    }

    updateControls() {
      const maxScrollLeft = Math.max(0, this.track.scrollWidth - this.track.clientWidth);

      if (this.previousButton) this.previousButton.disabled = this.track.scrollLeft <= 1;
      if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScrollLeft - 1;
    }
  }

  customElements.define(componentName, VLReviewCardSlider);
})();
